import Utils from '.';
import { httpConstants } from '../constants';
import ShowToast, { ToastTypeEnum } from '../common/components/ShowToast';

const EMPTY_ORG_ID = 'Empty';
const LOCAL_STORAGE_ORG_KEY = 'current_organization';

const getCurrentOrganizationId = (): string =>
  localStorage.getItem(LOCAL_STORAGE_ORG_KEY) || EMPTY_ORG_ID;

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

const handleUnauthorized = (callbackSlugOverride?: string) => {
  // 1) clear tokens/cookies
  Utils.clearTokenCookies();

  // 2) Check if the current location is already a login page
  const currentUrl = window.location.pathname;
  const fullUrl = window.location.href;
  const isLoginPage = currentUrl.endsWith('/login') || currentUrl.includes('/login');

  let callbackSlug = callbackSlugOverride || null;

  if (!callbackSlug && currentUrl.endsWith('/callback') && fullUrl.includes('state=')) {
    const stateParam = fullUrl.split('state=')[1]?.split('&')[0];
    const parts = stateParam?.split('%26');
    if (parts?.length > 2) {
      callbackSlug = parts[2];
    }
  }

  localStorage.setItem(
    'LogoutToast',
    callbackSlug
      ? 'Email domain not authorized for this organization'
      : 'Your session has expired. Please sign in again.'
  );

  if (!isLoginPage) {
    // 3) redirect to tenant-aware login
    const slug = localStorage.getItem('tenant-slug') || 'thehackettgroup';
    window.location.href = `/${callbackSlug || slug}/login`;
  }
};

const shouldSkipOrganizationHeader = (requestUrl: string): boolean => {
  try {
    const url = new URL(requestUrl, window.location.origin);
    const path = url.pathname;

    return (
      path === '/users' ||
      path.startsWith('/users/') ||
      path === '/organizations' ||
      path.startsWith('/organizations') ||
      path === '/organizations-lite' ||
      path.startsWith('/organizations-lite') ||
      path === '/api/v2/user' ||
      path.startsWith('/api/v2/user/') ||
      path.startsWith('/api/v1/e2e_taxonomy/ai-opportunity-popular')
    );
  } catch {
    return false;
  }
};

/**
 * Defines the structure of HTTP request options.
 */

/**
 * Creates request options for fetch API calls.
 *
 * @param method - The HTTP method (e.g., 'GET', 'POST', etc.).
 * @param data - The data to be sent in the request body.
 * @param headers - Optional headers for the request.
 * @returns The formatted request options.
 */
const createRequestOptions = (
  method: string,
  data?: unknown,
  headers?: HeadersInit
): RequestInit => {
  const { GET } = httpConstants.METHOD_TYPE;

  // Remove Content-Type for FormData (browser sets it automatically)
  const isFormData = data instanceof FormData;
  const requestOptions: RequestInit = {
    method,
    headers: isFormData ? headers : { 'Content-Type': 'application/json', ...headers },
    body: method !== GET ? (isFormData ? data : JSON.stringify(data)) : undefined,
  };

  return requestOptions;
};

export const httpService = async <T>(
  method: string,
  url: string,
  data?: unknown,
  headers?: HeadersInit,
  is_auth: boolean = true,
  x_org_id?: string
): Promise<T> => {
  const token = Utils.getCookie('auth-token');
  if (is_auth) {
    headers = {
      ...headers,
      Authorization: `Bearer ${token}`,
    };
  }
  if (!shouldSkipOrganizationHeader(url)) {
    const existingHeaders = headers as Record<string, string>;
    if (!existingHeaders?.['x-organization-id']) {
      headers = {
        ...headers,
        'x-organization-id': x_org_id || getCurrentOrganizationId(),
      };
    }
  }
  const requestOptions = createRequestOptions(method, data, headers);

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      let errorBody: any = {};
      try {
        const contentType = response.headers.get('Content-Type');
        if (contentType?.includes('application/json')) {
          errorBody = await response.json();
        } else {
          errorBody = { detail: await response.text() };
        }
      } catch {
        errorBody = {};
      }
      // ─── global session-expiry fallback ───────────────────────────────
      if (response.status === 401) {
        if (!isRefreshing) {
          isRefreshing = true;
          const newToken = await Utils.refreshToken();
          isRefreshing = false;

          if (newToken) {
            onRefreshed(newToken);
            // Retry original request
            return httpService(method, url, data, headers, is_auth, x_org_id);
          } else {
            // Refresh failed, proceed to logout
            handleUnauthorized();
          }
        } else {
          // Wait for refresh to complete
          return new Promise((resolve) => {
            subscribeTokenRefresh(() => {
              // Retry original request (it will pick up new token from cookies)
              resolve(httpService(method, url, data, headers, is_auth, x_org_id));
            });
          });
        }
      }

      if (response.status === 403) {
        ShowToast({
          message: 'You do not have permission to perform this action.',
          type: ToastTypeEnum.Error,
        });
      }

      // still reject so callers can handle (page will reload anyway)
      return Promise.reject({
        status: response.status,
        message: errorBody?.detail || errorBody?.message || response.statusText,
        details: errorBody?.detail,
      });
    }

    const contentType = response.headers.get('Content-Type');

    if (contentType?.includes('application/json')) {
      return (await response.json()) as T;
    } else {
      return (await response.text()) as unknown as T;
    }
  } catch (err) {
    return Promise.reject(err);
  }
};
